/**
 * Admin screens: the "my leagues" directory, league creation, and the per-league
 * admin console (settings, targets, rounds, roster, members, history).
 *
 * Reads are public; every mutation here needs a session, so the signed-out view says
 * so instead of pretending the list is empty. Scores never pass through a float:
 * every number the operator types goes through `parseScaled` and every number shown
 * comes back through `formatScaled`.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, formatScaled, maxFractionDigits, parseScaled } from "../api";
import { downscale, formatBytes, imageUrl, thumbUrl } from "../images";
import type {
  Account,
  AuditEntry,
  Division,
  Kind,
  League,
  LeagueDetail,
  MemberRole,
  Round,
  ScoreMode,
  Shooter,
  ShooterStatus,
  SignupMode,
  TargetTemplate,
} from "../types";
import {
  ApiError,
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Link,
  List,
  Notice,
  Row,
  Select,
  Spinner,
  Stack,
  Table,
  Tabs,
  Textarea,
  Warning,
  classNames,
  navigate,
  useAsync,
  useAuth,
  useLeague,
  formatDay,
  formatStamp,
} from "../components";

// --- shared bits -------------------------------------------------------------------------

/** Minimal view of `useAsync`'s return so tabs can receive a loaded resource. */
type Loader<T> = { data: T | null; loading: boolean; error: unknown; reload: () => void };

/**
 * `GET /leagues/{id}/settings` has no `api.ts` method, so the join token is read with
 * a plain fetch — same-origin credentials, never cached, same error shape as the api.
 */
type SettingsDoc = { signup_mode?: string; signup_token?: string | null; token?: string | null };

function loadSettings(id: string): Promise<SettingsDoc> {
  return fetch(`/api/v1/leagues/${id}/settings`, {
    credentials: "same-origin",
    cache: "no-store",
  }).then((response) =>
    response.ok
      ? (response.json() as Promise<SettingsDoc>)
      : Promise.reject(new ApiError(response.status, "Could not load settings")),
  );
}

function clipJson(value: unknown): string {
  const text = JSON.stringify(value) ?? "";
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}

// --- league directory ("my leagues") -------------------------------------------------------

export function LeagueDirectoryPage() {
  const auth = useAuth();
  const leagues = useAsync(() => api.leagues(), []);

  if (auth.loading) return <Spinner label="Checking session" />;

  if (auth.user === null) {
    return (
      <Card title="My leagues">
        <Notice>
          Every change on this page needs a session. <Link to="/login">Log in</Link> to manage
          leagues; the league directory itself stays public.
        </Notice>
      </Card>
    );
  }

  const mine = (leagues.data ?? []).filter((league) => league.can_manage || league.can_score);

  return (
    <Card
      title="My leagues"
      actions={
        <Row>
          {auth.user.is_site_admin ? (
            <Link to="/admin/accounts" className="btn btn-ghost">
              Accounts
            </Link>
          ) : null}
          <Link to="/admin/new" className="btn btn-primary">
            New league
          </Link>
        </Row>
      }
    >
      <Stack>
        <p className="muted small">
          Leagues where you are an admin or a scorer. There is no separate list — this is
          everything you can manage.
        </p>
        {leagues.loading ? <Spinner label="Loading leagues" /> : null}
        {leagues.error !== null ? (
          <ErrorNote error={leagues.error} />
        ) : null}
        {leagues.error === null && !leagues.loading && mine.length === 0 ? (
          <Empty
            title="You do not manage any leagues yet"
            note="Create a league and you become its admin, or ask an existing league admin to add you as a scorer."
            action={
              <Link to="/admin/new" className="btn btn-primary">
                New league
              </Link>
            }
          />
        ) : null}
        {!leagues.loading && mine.length > 0 ? (
          <div className="entry-list">
            {mine.map((league) => (
              <Link key={league.id} to={`/admin/${league.slug}`} className="entry-item">
                <span>{league.name}</span>
                <Badge tone={league.can_manage ? "info" : "neutral"}>
                  {league.can_manage ? "admin" : "scorer"}
                </Badge>
                <span className="spacer" />
                <span className="small muted num">
                  {league.rounds_count === 1 ? "1 round" : `${league.rounds_count} rounds`}
                </span>
              </Link>
            ))}
          </div>
        ) : null}
      </Stack>
    </Card>
  );
}

// --- new league -----------------------------------------------------------------------------

const KIND_CHOICES: { value: Kind; label: string }[] = [
  { value: "league", label: "league" },
  { value: "tournament", label: "tournament" },
];

const SCALE_CHOICES: { value: number; label: string }[] = [
  { value: 1, label: "whole numbers" },
  { value: 10, label: "tenths (0.1)" },
  { value: 100, label: "hundredths (0.01)" },
];

export function AdminNewLeaguePage() {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<Kind>("league");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [scale, setScale] = useState(1);
  const [signupMode, setSignupMode] = useState<SignupMode>("open");
  const [labelTemplate, setLabelTemplate] = useState("Week {n}");
  const [error, setError] = useState<unknown>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create(): Promise<void> {
    if (name.trim() === "") {
      setFieldError("A league needs a name.");
      return;
    }
    setFieldError(null);
    setError(null);
    setBusy(true);
    try {
      const league = await api.createLeague({
        name: name.trim(),
        kind,
        summary: summary.trim(),
        description,
        score_scale: scale,
        signup_mode: signupMode,
        label_template: labelTemplate.trim() === "" ? "Round {n}" : labelTemplate.trim(),
      });
      navigate(`/admin/${league.slug}`);
    } catch (cause) {
      setError(cause);
      setBusy(false);
    }
  }

  return (
    <Card title="New league">
      <Stack>
        <p className="muted small">
          You become the league admin. The page is public to read the moment it exists.
        </p>
        {error !== null ? <ErrorNote error={error} /> : null}
        <Field label="Name" htmlFor="league-name" error={fieldError ?? undefined}>
          <Input
            id="league-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={fieldError ? "input-bad" : undefined}
          />
        </Field>
        <Field label="Kind">
          <div className="row row-wrap" role="group" aria-label="Kind">
            {KIND_CHOICES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                aria-pressed={kind === choice.value}
                className={classNames("chip", kind === choice.value && "chip-active")}
                onClick={() => setKind(choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Summary" htmlFor="league-summary">
          <Input
            id="league-summary"
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
          />
          <p className="field-hint">One line, shown in the league directory.</p>
        </Field>
        <Field
          label="Description"
          htmlFor="league-description"
          hint="Location, times and rules go here. This renders on the public league page."
        >
          <Textarea
            id="league-description"
            rows={6}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field
          label="Score precision"
          hint="The smallest score this league records. It cannot be changed after scores exist."
        >
          <div className="row row-wrap" role="group" aria-label="Score precision">
            {SCALE_CHOICES.map((choice) => (
              <button
                key={choice.value}
                type="button"
                aria-pressed={scale === choice.value}
                className={classNames("chip", scale === choice.value && "chip-active")}
                onClick={() => setScale(choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </Field>
        <Field
          label="Joining"
          hint="Reading the league is always public. Only joining is gated."
        >
          <div className="row row-wrap" role="group" aria-label="Joining">
            <button
              type="button"
              aria-pressed={signupMode === "open"}
              className={classNames("chip", signupMode === "open" && "chip-active")}
              onClick={() => setSignupMode("open")}
            >
              open
            </button>
            <button
              type="button"
              aria-pressed={signupMode === "token"}
              className={classNames("chip", signupMode === "token" && "chip-active")}
              onClick={() => setSignupMode("token")}
            >
              token by invitation
            </button>
          </div>
        </Field>
        <Field label="Round label" htmlFor="league-label-template">
          <Input
            id="league-label-template"
            value={labelTemplate}
            onChange={(event) => setLabelTemplate(event.target.value)}
          />
          <p className="field-hint">Round labels; {"{n}"} becomes 1, 2, 3.</p>
        </Field>
        <div className="row">
          <Button variant="primary" busy={busy} onClick={create}>
            Create league
          </Button>
          <Link to="/admin" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </Stack>
    </Card>
  );
}

// --- league admin console -------------------------------------------------------------------

type AdminTab = "overview" | "targets" | "rounds" | "roster" | "members" | "history";

const ADMIN_TABS: { id: AdminTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "targets", label: "Targets" },
  { id: "rounds", label: "Rounds" },
  { id: "roster", label: "Roster" },
  { id: "members", label: "Members" },
  { id: "history", label: "History" },
];

export function AdminLeaguePage({ slug }: { slug: string }) {
  const auth = useAuth();
  const { league, loading, error, reload } = useLeague(slug);
  const detail = useAsync(() => api.league(slug), [slug]);
  const [tab, setTab] = useState<AdminTab>("overview");

  if (auth.loading) return <Spinner label="Checking session" />;
  if (auth.user === null) {
    return (
      <Card title="League admin">
        <Notice>
          <Link to="/login">Log in</Link> to administer this league. The public league page
          stays at <Link to={`/${slug}`}>/{slug}</Link>.
        </Notice>
      </Card>
    );
  }
  if (loading || (league === null && detail.loading)) {
    return <Spinner label="Loading league" />;
  }
  if (league === null) {
    return (
      <Card title="League admin">
        <Stack>
          <ErrorNote error={error ?? detail.error ?? new Error("League not found")} />
          <Link to="/admin" className="btn btn-ghost">
            Back to my leagues
          </Link>
        </Stack>
      </Card>
    );
  }
  if (!league.can_manage && !league.can_score) {
    return (
      <Card title={league.name}>
        <Notice>
          You are not a member of this league, so there is nothing to administer. You can
          still read it at <Link to={`/${slug}`}>/{slug}</Link>.
        </Notice>
      </Card>
    );
  }

  const divisions: Division[] = detail.data?.divisions ?? [];

  return (
    <Stack>
      <Card
        title={league.name}
        actions={
          <Link to={`/${slug}`} className="btn btn-ghost btn-small">
            Public page
          </Link>
        }
      >
        <p className="muted small">
          {league.slug} · scores in {SCALE_CHOICES.find((choice) => choice.value === league.score_scale)?.label ?? `1/${league.score_scale}`}.{" "}
          <Badge tone={league.can_manage ? "info" : "neutral"}>
            {league.can_manage ? "you are an admin" : "you are a scorer"}
          </Badge>
        </p>
      </Card>
      <Tabs tabs={ADMIN_TABS} active={tab} onChange={setTab} />
      {tab === "overview" ? <OverviewTab league={league} slug={slug} reload={reload} /> : null}
      {tab === "targets" ? <TargetsTab league={league} /> : null}
      {tab === "rounds" ? (
        <RoundsTab
          league={league}
          slug={slug}
          detail={detail}
        />
      ) : null}
      {tab === "roster" ? <RosterTab league={league} divisions={divisions} /> : null}
      {tab === "members" ? <MembersTab league={league} /> : null}
      {tab === "history" ? <HistoryTab league={league} /> : null}
    </Stack>
  );
}

// --- overview -------------------------------------------------------------------------------

function OverviewTab({
  league,
  slug,
  reload,
}: {
  league: League;
  slug: string;
  reload: () => void;
}) {
  const [name, setName] = useState(league.name);
  const [summary, setSummary] = useState(league.summary);
  const [description, setDescription] = useState(league.description);
  const [timezone, setTimezone] = useState(league.timezone);
  const [labelTemplate, setLabelTemplate] = useState(league.label_template);
  const [signupMode, setSignupMode] = useState<SignupMode>(league.signup_mode);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [rotating, setRotating] = useState(false);

  const settings = useAsync(() => loadSettings(league.id), [league.id, league.signup_mode]);
  const token = settings.data?.signup_token ?? settings.data?.token ?? null;

  function edit(setter: (value: string) => void) {
    return (event: { target: { value: string } }): void => {
      setter(event.target.value);
      setSaved(false);
    };
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.patchLeague(league.id, {
        name,
        summary,
        description,
        timezone,
        label_template: labelTemplate,
        signup_mode: signupMode,
      });
      setSaved(true);
      reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function rotateToken(): Promise<void> {
    setBusy(true);
    setRotating(true);
    setError(null);
    try {
      await api.patchLeague(league.id, { rotate_signup_token: true });
      settings.reload();
      reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
      setRotating(false);
    }
  }

  return (
    <Stack>
      <Card title="Settings">
        <Stack>
          {error !== null ? <ErrorNote error={error} /> : null}
          <Field label="Name" htmlFor="admin-name">
            <Input id="admin-name" value={name} onChange={edit(setName)} />
          </Field>
          <Field label="Summary" htmlFor="admin-summary">
            <Input
              id="admin-summary"
              value={summary}
              onChange={edit(setSummary)}
            />
            <p className="field-hint">One line, shown in the league directory.</p>
          </Field>
          <Field
            label="Description"
            htmlFor="admin-description"
            hint="Location, times and rules go here. This renders on the public league page."
          >
            <Textarea
              id="admin-description"
              rows={6}
              value={description}
              onChange={edit(setDescription)}
            />
          </Field>
          <Field
            label="Timezone"
            htmlFor="admin-timezone"
            hint="IANA name like America/New_York; controls round dates and “tonight”."
          >
            <Input id="admin-timezone" value={timezone} onChange={edit(setTimezone)} />
          </Field>
          <Field label="Round label" htmlFor="admin-label-template">
            <Input
              id="admin-label-template"
              value={labelTemplate}
              onChange={edit(setLabelTemplate)}
            />
            <p className="field-hint">Round labels; {"{n}"} becomes 1, 2, 3.</p>
          </Field>
          <Field label="Joining" htmlFor="admin-signup-mode">
            <Select
              id="admin-signup-mode"
              value={signupMode}
              onChange={(event) => {
                setSignupMode(event.target.value as SignupMode);
                setSaved(false);
              }}
            >
              <option value="open">open — anyone with the page can join</option>
              <option value="token">token — invitation only</option>
            </Select>
            <p className="field-hint">Reading the league is always public. Only joining is gated.</p>
          </Field>
          <div className="row">
            <Button variant="primary" busy={busy} onClick={save}>
              Save
            </Button>
            {saved ? (
              <p className="saved-flash" role="status">
                Saved
              </p>
            ) : null}
          </div>
        </Stack>
      </Card>
      <Card title="Joining">
        <Stack>
          <p className="small muted">
            Current mode:{" "}
            <Badge tone={league.signup_mode === "token" ? "warn" : "good"}>
              {league.signup_mode === "token" ? "token" : "open"}
            </Badge>
          </p>
          {league.signup_mode === "token" ? (
            <Stack tight>
              {settings.loading ? <Spinner label="Loading token" /> : null}
              {settings.error !== null ? <ErrorNote error={settings.error} /> : null}
              {settings.data !== null && token === null ? (
                <p className="muted small">
                  No token is set yet. Rotate to generate one.
                </p>
              ) : null}
              {token !== null ? (
                <Stack tight>
                  <p className="mono">{token}</p>
                  <Field label="Share link">
                    <Input
                      readOnly
                      value={`${window.location.origin}/${slug}/join?token=${token}`}
                      onFocus={(event) => event.currentTarget.select()}
                    />
                  </Field>
                  <Warning>
                    Rotating stops the old link working immediately; anyone holding it cannot
                    join until you send the new one.
                  </Warning>
                  <div>
                    <Button busy={rotating} onClick={rotateToken}>
                      Rotate token
                    </Button>
                  </div>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <p className="muted small">
              Anyone with the league page can join. Switch the mode above to require an
              invitation token.
            </p>
          )}
        </Stack>
      </Card>
      <LeaguePhotosCard leagueId={league.id} />
    </Stack>
  );
}

// --- league page photos -------------------------------------------------------------------
// Page pictures decorate the public league page and delete cleanly, file and row together.
// Target evidence is the opposite animal: it has no delete route at all, so this card is
// the only place in the admin where a picture can be removed.

function LeaguePhotosCard({ leagueId }: { leagueId: string }) {
  const photos = useAsync(() => api.leagueImages(leagueId), [leagueId]);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [captionEdits, setCaptionEdits] = useState<Record<string, string>>({});
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const rows = photos.data?.images ?? [];

  async function add(): Promise<void> {
    if (file === null) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await downscale(file);
      const form = new FormData();
      form.set("file", blob, file.name === "" ? "photo.jpg" : file.name);
      if (caption.trim() !== "") form.set("caption", caption.trim());
      await api.uploadLeagueImage(leagueId, form);
      setFile(null);
      setCaption("");
      photos.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function saveCaption(imageId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.patchLeagueImage(leagueId, imageId, {
        caption: (captionEdits[imageId] ?? "").trim(),
      });
      setCaptionEdits((current) => {
        const next = { ...current };
        delete next[imageId];
        return next;
      });
      photos.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function remove(imageId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.deleteLeagueImage(leagueId, imageId);
      setConfirmId(null);
      photos.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="League page photos">
      <Stack>
        <p className="muted small">
          Pictures for the public league page — they are not score evidence, and deleting
          one removes the file too. Uploads join the page in order, most recent last.
        </p>
        {error !== null ? <ErrorNote error={error} /> : null}
        <Field
          label="Picture"
          hint="JPEG, PNG or WebP; the browser downscales before upload and the server caps the size."
        >
          <input
            type="file"
            accept="image/*"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </Field>
        <div className="row row-wrap">
          <Input
            placeholder="Caption (optional)"
            value={caption}
            maxLength={200}
            onChange={(event) => setCaption(event.target.value)}
          />
          <Button variant="primary" busy={busy} disabled={file === null} onClick={add}>
            Upload
          </Button>
        </div>
        {photos.loading ? <Spinner label="Loading photos" /> : null}
        {photos.error !== null ? <ErrorNote error={photos.error} /> : null}
        {rows.length === 0 && !photos.loading ? (
          <p className="muted small">No pictures on the league page yet.</p>
        ) : null}
        {rows.length === 0 ? null : (
          <Table
            head={["Photo", "Caption", null]}
            rows={rows.map((photo) => {
              const draft = captionEdits[photo.id] ?? photo.caption;
              return [
                <div className="admin-photo-row">
                  <img src={thumbUrl(photo.id)} alt="" loading="lazy" />
                  <span className="stack stack-tight">
                    <a href={imageUrl(photo.id)}>Full photo</a>
                    <span className="muted small">
                      {photo.width}×{photo.height} · {formatBytes(photo.bytes)}
                    </span>
                  </span>
                </div>,
                <Input
                  value={draft}
                  placeholder="(no caption)"
                  maxLength={200}
                  onChange={(event) =>
                    setCaptionEdits((current) => ({ ...current, [photo.id]: event.target.value }))
                  }
                />,
                <div className="row row-wrap">
                  {draft !== photo.caption ? (
                    <Button busy={busy} onClick={() => void saveCaption(photo.id)}>
                      Save caption
                    </Button>
                  ) : null}
                  <Button
                    disabled={busy}
                    onClick={() => {
                      if (confirmId !== photo.id) setConfirmId(photo.id);
                      else void remove(photo.id);
                    }}
                  >
                    {confirmId === photo.id ? "Confirm delete" : "Delete"}
                  </Button>
                </div>,
              ];
            })}
          />
        )}
      </Stack>
    </Card>
  );
}

// --- targets -----------------------------------------------------------------------------------

function TargetsTab({ league }: { league: League }) {
  const templates = useAsync(() => api.templates(league.id), [league.id]);
  const scale = league.score_scale;

  const [name, setName] = useState("");
  const [mode, setMode] = useState<ScoreMode>("points");
  const [shots, setShots] = useState("");
  const [maxText, setMaxText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editMode, setEditMode] = useState<ScoreMode>("points");
  const [editShots, setEditShots] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (templates.loading) return <Spinner label="Loading targets" />;

  const list = templates.data ?? [];
  const digits = maxFractionDigits(scale);

  function parseShots(text: string): number | undefined | "invalid" {
    if (text.trim() === "") return undefined;
    const value = Number(text.trim());
    return Number.isInteger(value) && value >= 1 ? value : "invalid";
  }

  function startEdit(template: TargetTemplate): void {
    setEditingId(template.id);
    setEditName(template.name);
    setEditMode(template.mode);
    setEditShots(template.shots === null ? "" : String(template.shots));
    setMaxText(formatScaled(template.max_value_scaled, scale));
    setConfirmId(null);
    setError(null);
  }

  function stopEdit(): void {
    setEditingId(null);
    setMaxText("");
  }

  async function add(): Promise<void> {
    const parsed = parseScaled(maxText, scale);
    if (name.trim() === "") {
      setError(new Error("Give the target a name."));
      return;
    }
    if (parsed === null) {
      setError(
        new Error(
          `Maximum must be a plain number${digits > 0 ? ` with at most ${digits} decimal places` : ""}.`,
        ),
      );
      return;
    }
    const shotsValue = parseShots(shots);
    if (shotsValue === "invalid") {
      setError(new Error("Shots must be a whole number."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createTemplate(league.id, {
        name: name.trim(),
        mode,
        shots: shotsValue,
        max_value_scaled: parsed,
      });
      setName("");
      setShots("");
      setMaxText("");
      templates.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(templateId: string): Promise<void> {
    const parsed = parseScaled(maxText, scale);
    if (editName.trim() === "") {
      setError(new Error("Give the target a name."));
      return;
    }
    if (parsed === null) {
      setError(new Error("Maximum must be a plain number."));
      return;
    }
    const shotsValue = parseShots(editShots);
    if (shotsValue === "invalid") {
      setError(new Error("Shots must be a whole number."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patchTemplate(league.id, templateId, {
        name: editName.trim(),
        mode: editMode,
        shots: shotsValue,
        max_value_scaled: parsed,
      });
      stopEdit();
      templates.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function remove(template: TargetTemplate): Promise<void> {
    if (confirmId !== template.id) {
      setConfirmId(template.id);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteTemplate(league.id, template.id);
      setConfirmId(null);
      templates.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const rows: ReactNode[][] = list.map((template) =>
    editingId === template.id
      ? [
          <Field label="Name">
            <Input value={editName} onChange={(event) => setEditName(event.target.value)} />
          </Field>,
          <Field label="Mode">
            <Select
              value={editMode}
              onChange={(event) => setEditMode(event.target.value as ScoreMode)}
            >
              <option value="points">points</option>
              <option value="hit_miss">hit_miss</option>
            </Select>
          </Field>,
          <Field label="Shots">
            <Input
              inputMode="numeric"
              value={editShots}
              onChange={(event) => setEditShots(event.target.value)}
            />
          </Field>,
          <Field label="Maximum">
            <Input
              inputMode="decimal"
              value={maxText}
              onChange={(event) => setMaxText(event.target.value)}
            />
          </Field>,
          <div className="row row-wrap">
            <Button variant="primary" busy={busy} onClick={() => saveEdit(template.id)}>
              Save
            </Button>
            <Button onClick={stopEdit}>Cancel</Button>
          </div>,
        ]
      : [
          template.name,
          template.mode,
          template.shots ?? <span className="muted">—</span>,
          <span className="num">{formatScaled(template.max_value_scaled, scale)}</span>,
          <div className="row row-wrap">
            <Button onClick={() => startEdit(template)}>Edit</Button>
            <Button variant="danger" busy={busy} onClick={() => remove(template)}>
              {confirmId === template.id ? "Confirm delete" : "Delete"}
            </Button>
          </div>,
        ],
  );

  return (
    <Stack>
      <Card title="Targets">
        <Stack>
          {templates.error !== null ? <ErrorNote error={templates.error} /> : null}
          {list.length === 0 ? (
            <Empty
              title="No targets yet"
              note="Targets are the scoring cards each round is composed from. Add the first one below."
            />
          ) : (
            <Table
              head={["Name", "Mode", "Shots", "Maximum", null]}
              rows={rows}
              align={["left", "left", "right", "right", "left"]}
            />
          )}
          <Notice>
            Editing a target does not change rounds already composed from it; each round keeps
            its own snapshot.
          </Notice>
        </Stack>
      </Card>
      <Card title="Add target">
        <Stack>
          {error !== null ? <ErrorNote error={error} /> : null}
          <Field label="Name" htmlFor="template-name">
            <Input id="template-name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          <Field label="Mode" htmlFor="template-mode">
            <Select
              id="template-mode"
              value={mode}
              onChange={(event) => setMode(event.target.value as ScoreMode)}
            >
              <option value="points">points</option>
              <option value="hit_miss">hit_miss</option>
            </Select>
          </Field>
          <Field
            label="Shots"
            htmlFor="template-shots"
            hint="Optional number of shots on this target."
          >
            <Input
              id="template-shots"
              inputMode="numeric"
              value={shots}
              onChange={(event) => setShots(event.target.value)}
            />
          </Field>
          <Field
            label="Maximum"
            htmlFor="template-max"
            hint={`Highest value on this target, in league scale. This league records ${SCALE_CHOICES.find((choice) => choice.value === scale)?.label ?? "that scale"}.`}
          >
            <Input
              id="template-max"
              inputMode="decimal"
              value={maxText}
              onChange={(event) => setMaxText(event.target.value)}
            />
          </Field>
          <div>
            <Button variant="primary" busy={busy} onClick={add}>
              Add target
            </Button>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}

// --- rounds -------------------------------------------------------------------------------------

function RoundsTab({
  league,
  slug,
  detail,
}: {
  league: League;
  slug: string;
  detail: Loader<LeagueDetail>;
}) {
  const templates = useAsync(() => api.templates(league.id), [league.id]);

  if (detail.loading) return <Spinner label="Loading rounds" />;
  if (detail.error !== null) return <ErrorNote error={detail.error} />;

  const rounds = detail.data?.rounds ?? [];
  const templateList = templates.data ?? [];

  return (
    <Stack>
      <Card title="New round">
        <NewRoundForm
          league={league}
          slug={slug}
          rounds={rounds}
          templates={templateList}
          templatesError={templates.error}
          onCreated={detail.reload}
        />
      </Card>
      <Card title={`Rounds (${rounds.length})`}>
        <Stack>
          {!league.can_manage ? (
            <Notice>Rounds are created and changed by a league admin.</Notice>
          ) : null}
          {rounds.length === 0 ? (
            <Empty
              title="No rounds yet"
              note="Create the first round above. Rounds that are still draft stay off the public page."
            />
          ) : (
            rounds.map((round) => (
              <RoundRow
                key={round.id}
                round={round}
                league={league}
                slug={slug}
                templates={templateList}
                canManage={league.can_manage}
                onChanged={detail.reload}
              />
            ))
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

function NewRoundForm({
  league,
  slug,
  rounds,
  templates,
  templatesError,
  onCreated,
}: {
  league: League;
  slug: string;
  rounds: Round[];
  templates: TargetTemplate[];
  templatesError: unknown;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState("");
  const [labelEdited, setLabelEdited] = useState(false);
  const [playsOn, setPlaysOn] = useState("");
  const [selected, setSelected] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [serverWarning, setServerWarning] = useState<string | null>(null);
  const defaultedFrom = useRef<string | null>(null);

  const lastRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
  const nextPosition = rounds.length + 1;

  useEffect(() => {
    if (!labelEdited) {
      setLabel(league.label_template.replace(/\{n\}/g, String(nextPosition)));
    }
  }, [labelEdited, nextPosition, league.label_template]);

  // Target set defaults to the previous round's snapshot; with no previous round,
  // every template is in.
  useEffect(() => {
    if (templates.length === 0 || defaultedFrom.current !== null) return;
    let cancelled = false;
    const apply = (ids: string[]): void => {
      if (cancelled) return;
      setSelected(ids);
      defaultedFrom.current = lastRound ? lastRound.id : "none";
    };
    if (lastRound) {
      void api
        .round(slug, lastRound.id)
        .then((view) => apply(view.targets.map((target) => target.target_id)))
        .catch(() => apply(templates.map((template) => template.id)));
    } else {
      apply(templates.map((template) => template.id));
    }
    return () => {
      cancelled = true;
    };
  }, [templates, lastRound, slug]);

  const chosen = selected ?? [];
  const chosenMax = templates
    .filter((template) => chosen.includes(template.id))
    .reduce((sum, template) => sum + template.max_value_scaled, 0);
  const otherMaxes = rounds.map((round) => round.max_total_scaled);
  const differs =
    chosen.length > 0 && otherMaxes.length > 0 && !otherMaxes.includes(chosenMax);

  function toggle(targetId: string): void {
    setSelected((current) => {
      const at = (current ?? []).indexOf(targetId);
      const next = [...(current ?? [])];
      if (at >= 0) next.splice(at, 1);
      else next.push(targetId);
      return next;
    });
  }

  async function create(): Promise<void> {
    if (label.trim() === "") {
      setError(new Error("Give the round a label."));
      return;
    }
    if (chosen.length === 0) {
      setError(new Error("Pick at least one target."));
      return;
    }
    setBusy(true);
    setError(null);
    setServerWarning(null);
    try {
      const created = await api.createRound(league.id, {
        label: label.trim(),
        plays_on: playsOn === "" ? undefined : playsOn,
        target_ids: chosen,
      });
      const warning = (created as Round & { warning?: string }).warning;
      if (warning !== undefined) setServerWarning(warning);
      setPlaysOn("");
      defaultedFrom.current = null;
      onCreated();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  if (!league.can_manage) {
    return (
      <Notice>
        Rounds are created and changed by a league admin. You can enter scores for the rounds
        they open.
      </Notice>
    );
  }

  return (
    <Stack>
      {templatesError !== null ? <ErrorNote error={templatesError} /> : null}
      {error !== null ? <ErrorNote error={error} /> : null}
      {serverWarning !== null ? <Warning>{serverWarning}</Warning> : null}
      <Field label="Label" htmlFor="round-label">
        <Input
          id="round-label"
          value={label}
          onChange={(event) => {
            setLabel(event.target.value);
            setLabelEdited(true);
          }}
        />
        <p className="field-hint">
          Prefilled from the league template; {"{n}"} becomes {nextPosition}.
        </p>
      </Field>
      <Field label="Plays on" htmlFor="round-plays-on">
        <Input id="round-plays-on" type="date" value={playsOn} onChange={(event) => setPlaysOn(event.target.value)} />
      </Field>
      <fieldset className="field">
        <legend>Targets</legend>
        {templates.length === 0 ? (
          <p className="entry-error">Add targets first — a round needs at least one.</p>
        ) : (
          templates.map((template) => (
            <label key={template.id} className="checkbox">
              <input
                type="checkbox"
                checked={chosen.includes(template.id)}
                onChange={() => toggle(template.id)}
              />
              <span>{template.name}</span>
              <span className="spacer" />
              <span className="entry-max num">{formatScaled(template.max_value_scaled, league.score_scale)}</span>
            </label>
          ))
        )}
      </fieldset>
      <p className="small muted">
        Round maximum:{" "}
        <span className="num">
          {formatScaled(chosenMax, league.score_scale)}
        </span>{" "}
        <span className="entry-max">
          ({chosen.length} of {templates.length} targets)
        </span>
      </p>
      {differs ? (
        <Warning>
          This maximum differs from other rounds in this league. It will be accepted, but
          flagged.
        </Warning>
      ) : null}
      <div>
        <Button variant="primary" busy={busy} onClick={create}>
          Add round
        </Button>
      </div>
    </Stack>
  );
}

function RoundRow({
  round,
  league,
  slug,
  templates,
  canManage,
  onChanged,
}: {
  round: Round;
  league: League;
  slug: string;
  templates: TargetTemplate[];
  canManage: boolean;
  onChanged: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [labelDraft, setLabelDraft] = useState(round.label);
  const [datesOpen, setDatesOpen] = useState(false);
  const [playsOn, setPlaysOn] = useState(round.plays_on ?? "");
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [selected, setSelected] = useState<string[] | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [serverWarning, setServerWarning] = useState<string | null>(null);
  const [targetsBusy, setTargetsBusy] = useState(false);

  async function patch(payload: {
    label?: string;
    plays_on?: string | null;
    status?: string;
    target_ids?: string[];
  }): Promise<void> {
    setBusy(true);
    setError(null);
    setServerWarning(null);
    try {
      const result = await api.patchRound(league.id, round.id, payload);
      if (result.warning !== undefined) setServerWarning(result.warning);
      onChanged();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  function openTargets(): void {
    const next = !targetsOpen;
    setTargetsOpen(next);
    setTargetsBusy(false);
    setError(null);
    if (next && selected === null) {
      setTargetsBusy(true);
      void api
        .round(slug, round.id)
        .then((view) => setSelected(view.targets.map((target) => target.target_id)))
        .catch((cause: unknown) => setError(cause))
        .finally(() => setTargetsBusy(false));
    }
  }

  async function remove(): Promise<void> {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.deleteRound(league.id, round.id);
      onChanged();
    } catch (cause) {
      setError(cause);
      setBusy(false);
    }
  }

  const chosen = selected ?? [];

  return (
    <Stack tight>
      <div className="entry-item">
        <span className="rank num">{round.position}</span>
        <span>{round.label}</span>
        <Badge tone={round.status === "open" ? "good" : round.status === "draft" ? "info" : "neutral"}>
          {round.status}
        </Badge>
        <span className="spacer" />
        <span className="small muted">{formatDay(round.plays_on)}</span>
        <span className="entry-max num">max {formatScaled(round.max_total_scaled, league.score_scale)}</span>
      </div>
      {canManage ? (
        <div className="row row-wrap">
          {round.status !== "open" ? (
            <Button busy={busy} onClick={() => patch({ status: "open" })}>
              Open
            </Button>
          ) : null}
          {round.status !== "locked" ? (
            <Button busy={busy} onClick={() => patch({ status: "locked" })}>
              Lock
            </Button>
          ) : null}
          {round.status !== "draft" ? (
            <Button busy={busy} onClick={() => patch({ status: "draft" })}>
              Mark draft
            </Button>
          ) : null}
          <Button onClick={() => setRenaming(!renaming)}>Rename</Button>
          <Button onClick={() => setDatesOpen(!datesOpen)}>Dates</Button>
          <Button onClick={openTargets}>Targets</Button>
          <Button variant="danger" busy={busy} onClick={remove}>
            {confirmDelete ? "Confirm delete" : "Delete"}
          </Button>
        </div>
      ) : null}
      {serverWarning !== null ? <Warning>{serverWarning}</Warning> : null}
      {error !== null ? <ErrorNote error={error} /> : null}
      {renaming ? (
        <div className="row row-wrap">
          <Field label="New label" htmlFor={`rename-${round.id}`}>
            <Input
              id={`rename-${round.id}`}
              value={labelDraft}
              onChange={(event) => setLabelDraft(event.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            busy={busy}
            onClick={() => {
              if (labelDraft.trim() === "") {
                setError(new Error("The label cannot be empty."));
                return;
              }
              setRenaming(false);
              void patch({ label: labelDraft.trim() });
            }}
          >
            Save label
          </Button>
          <Button onClick={() => setRenaming(false)}>Cancel</Button>
        </div>
      ) : null}
      {datesOpen ? (
        <div className="row row-wrap">
          <Field label="Plays on" htmlFor={`date-${round.id}`} hint="Leave empty to unschedule.">
            <Input
              id={`date-${round.id}`}
              type="date"
              value={playsOn}
              onChange={(event) => setPlaysOn(event.target.value)}
            />
          </Field>
          <Button
            variant="primary"
            busy={busy}
            onClick={() => {
              setDatesOpen(false);
              void patch({ plays_on: playsOn === "" ? null : playsOn });
            }}
          >
            Save date
          </Button>
          <Button onClick={() => setDatesOpen(false)}>Cancel</Button>
        </div>
      ) : null}
      {targetsOpen ? (
        <div className="stack stack-tight">
          {targetsBusy ? <Spinner label="Loading targets" /> : null}
          {selected !== null ? (
            <Stack tight>
              <p className="section-title">Targets in this round</p>
              {templates.map((template) => (
                <label key={template.id} className="checkbox">
                  <input
                    type="checkbox"
                    checked={chosen.includes(template.id)}
                    onChange={() =>
                      setSelected((current) => {
                        const at = (current ?? []).indexOf(template.id);
                        const next = [...(current ?? [])];
                        if (at >= 0) next.splice(at, 1);
                        else next.push(template.id);
                        return next;
                      })
                    }
                  />
                  <span>{template.name}</span>
                  <span className="spacer" />
                  <span className="entry-max num">
                    {formatScaled(template.max_value_scaled, league.score_scale)}
                  </span>
                </label>
              ))}
              {chosen.length === 0 ? (
                <p className="entry-error">Pick at least one target.</p>
              ) : null}
              <p className="small muted">
                New maximum:{" "}
                <span className="num">
                  {formatScaled(
                    templates
                      .filter((template) => chosen.includes(template.id))
                      .reduce((sum, template) => sum + template.max_value_scaled, 0),
                    league.score_scale,
                  )}
                </span>
                . Scores already entered for targets removed here are unaffected until you
                re-add them.
              </p>
              <div>
                <Button
                  variant="primary"
                  busy={busy}
                  onClick={() => {
                    if (chosen.length === 0) return;
                    setTargetsOpen(false);
                    void patch({ target_ids: chosen });
                  }}
                >
                  Save targets
                </Button>
              </div>
            </Stack>
          ) : null}
        </div>
      ) : null}
    </Stack>
  );
}

// --- roster ------------------------------------------------------------------------------------

type RosterFilter = "all" | ShooterStatus;

const ROSTER_FILTERS: RosterFilter[] = ["all", "pending", "active", "retired"];

function RosterTab({ league, divisions }: { league: League; divisions: Division[] }) {
  const all = useAsync(() => api.roster(league.id, "all"), [league.id]);
  const [filter, setFilter] = useState<RosterFilter>("all");
  const [touchedFilter, setTouchedFilter] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Shooter | null>(null);
  const [editGiven, setEditGiven] = useState("");
  const [editFamily, setEditFamily] = useState("");
  const [editDisamb, setEditDisamb] = useState("");
  const [editDivision, setEditDivision] = useState("");
  const [editPublic, setEditPublic] = useState(true);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [newGiven, setNewGiven] = useState("");
  const [newFamily, setNewFamily] = useState("");
  const [newDivision, setNewDivision] = useState("");

  const rows = all.data?.shooters ?? [];
  // The server counts pending across the whole roster; a filtered view would undercount it.
  const pendingCount = all.data?.pending_count ?? 0;

  useEffect(() => {
    if (touchedFilter || all.data === null) return;
    // Opening on the queue is the point — but the moment it is empty the shooter you just
    // approved would vanish under you, so the suggestion lets go and shows the roster.
    setFilter(pendingCount > 0 ? "pending" : "all");
  }, [all.data, pendingCount, touchedFilter]);

  if (all.loading) return <Spinner label="Loading roster" />;

  const visible = rows.filter((shooter) => filter === "all" || shooter.status === filter);
  const pending = rows.filter((shooter) => shooter.status === "pending");

  async function decide(shooter: Shooter, decision: "approve" | "decline"): Promise<void> {
    setBusyId(shooter.id);
    setError(null);
    try {
      await api.decideShooter(league.id, shooter.id, decision);
      all.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  }

  function startEdit(shooter: Shooter): void {
    setEditing(shooter);
    setEditGiven(shooter.given_name);
    setEditFamily(shooter.family_name);
    setEditDisamb(shooter.disambiguator ?? "");
    setEditDivision(shooter.division_id ?? "");
    setEditPublic(shooter.is_public);
    setError(null);
    setConfirmDeleteId(null);
  }

  async function saveEdit(): Promise<void> {
    if (editing === null) return;
    if (editGiven.trim() === "" && editFamily.trim() === "") {
      setError(new Error("A shooter needs at least one name."));
      return;
    }
    setBusyId(editing.id);
    setError(null);
    try {
      await api.patchShooter(league.id, editing.id, {
        given_name: editGiven.trim(),
        family_name: editFamily.trim(),
        disambiguator: editDisamb.trim() === "" ? null : editDisamb.trim(),
        division_id: editDivision === "" ? null : editDivision,
        is_public: editPublic,
      });
      setEditing(null);
      all.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  }

  async function retire(shooter: Shooter): Promise<void> {
    setBusyId(shooter.id);
    setError(null);
    try {
      await api.retireShooter(league.id, shooter.id);
      all.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(shooter: Shooter): Promise<void> {
    if (confirmDeleteId !== shooter.id) {
      setConfirmDeleteId(shooter.id);
      return;
    }
    setBusyId(shooter.id);
    setError(null);
    try {
      await api.deleteShooter(league.id, shooter.id);
      setConfirmDeleteId(null);
      all.reload();
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 409
          ? new Error(`${cause.detail} Retire the shooter instead — their scores stand.`)
          : cause,
      );
      setConfirmDeleteId(null);
    } finally {
      setBusyId(null);
    }
  }

  async function addShooter(): Promise<void> {
    if (newGiven.trim() === "" || newFamily.trim() === "") {
      setError(new Error("A shooter needs a first and a last name."));
      return;
    }
    setBusyId("new");
    setError(null);
    try {
      await api.createShooter(league.id, {
        given_name: newGiven.trim(),
        family_name: newFamily.trim(),
        division_id: newDivision === "" ? undefined : newDivision,
      });
      setNewGiven("");
      setNewFamily("");
      setNewDivision("");
      all.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Stack>
      <Card title="Pending sign-ups">
        <Stack>
          <Notice>
            Either role — league admin or scorer — may approve sign-ups. Approving makes the
            shooter visible everywhere, standings included. Declining deletes the request.
          </Notice>
          {pending.length === 0 ? (
            <p className="muted small">Nothing is waiting right now.</p>
          ) : (
            <div className="entry-list">
              {pending.map((shooter) => (
                <div key={shooter.id} className="entry-item">
                  <span>{shooter.display_name}</span>
                  <span className="small muted">{formatStamp(shooter.joined_at)}</span>
                  <span className="spacer" />
                  <Button
                    variant="primary"
                    busy={busyId === shooter.id}
                    onClick={() => decide(shooter, "approve")}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="danger"
                    busy={busyId === shooter.id}
                    onClick={() => decide(shooter, "decline")}
                  >
                    Decline
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Stack>
      </Card>
      <Card
        title="Roster"
        actions={<span className="small muted num">{`${rows.length} total`}</span>}
      >
        <Stack>
          {error !== null ? <ErrorNote error={error} /> : null}
          <Row wrap>
            {ROSTER_FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={filter === option}
                className={classNames("chip", filter === option && "chip-active")}
                onClick={() => {
                  setTouchedFilter(true);
                  setFilter(option);
                }}
              >
                {option}
                {option === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
              </button>
            ))}
          </Row>
          {visible.length === 0 ? (
            <Empty
              title={
                pendingCount > 0 && filter === "pending"
                  ? "Nothing waiting"
                  : "No shooters in this view"
              }
              note="Switch the filter above, or add a shooter below."
            />
          ) : (
            <div className="entry-list">
              {visible.map((shooter) => {
                const divisionLabel =
                  divisions.find((division) => division.id === shooter.division_id)?.name ?? null;
                return (
                  <div key={shooter.id} className="entry-item">
                    <span>{shooter.display_name}</span>
                    {shooter.disambiguator !== null ? (
                      <span className="small muted">({shooter.disambiguator})</span>
                    ) : null}
                    {divisionLabel !== null ? (
                      <span className="small muted">{divisionLabel}</span>
                    ) : null}
                    <Badge
                      tone={
                        shooter.status === "active"
                          ? "good"
                          : shooter.status === "pending"
                            ? "info"
                            : "neutral"
                      }
                    >
                      {shooter.status}
                    </Badge>
                    {shooter.is_public ? null : <Badge tone="warn">private</Badge>}
                    <span className="spacer" />
                    {league.can_manage ? (
                      <span className="row row-wrap">
                        <Button onClick={() => startEdit(shooter)}>Edit</Button>
                        {shooter.status === "active" ? (
                          <Button busy={busyId === shooter.id} onClick={() => retire(shooter)}>
                            Retire
                          </Button>
                        ) : null}
                        <Button
                          variant="danger"
                          busy={busyId === shooter.id}
                          onClick={() => remove(shooter)}
                        >
                          {confirmDeleteId === shooter.id ? "Confirm delete" : "Delete"}
                        </Button>
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          {editing !== null && league.can_manage ? (
            <Card title={`Edit ${editing.display_name}`}>
              <Stack>
                <Field label="First name" htmlFor={`given-${editing.id}`}>
                  <Input
                    id={`given-${editing.id}`}
                    value={editGiven}
                    onChange={(event) => setEditGiven(event.target.value)}
                  />
                </Field>
                <Field label="Last name" htmlFor={`family-${editing.id}`}>
                  <Input
                    id={`family-${editing.id}`}
                    value={editFamily}
                    onChange={(event) => setEditFamily(event.target.value)}
                  />
                </Field>
                <Field
                  label="Disambiguator"
                  htmlFor={`disamb-${editing.id}`}
                  hint="Shown after the name when two shooters share one. Empty clears it."
                >
                  <Input
                    id={`disamb-${editing.id}`}
                    value={editDisamb}
                    onChange={(event) => setEditDisamb(event.target.value)}
                  />
                </Field>
                <Field label="Division" htmlFor={`division-${editing.id}`}>
                  <Select
                    id={`division-${editing.id}`}
                    value={editDivision}
                    onChange={(event) => setEditDivision(event.target.value)}
                  >
                    <option value="">No division</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={editPublic}
                    onChange={(event) => setEditPublic(event.target.checked)}
                  />
                  <span>Public — this shooter appears on standings and profiles</span>
                </label>
                <div className="row row-wrap">
                  <Button variant="primary" busy={busyId === editing.id} onClick={saveEdit}>
                    Save shooter
                  </Button>
                  <Button onClick={() => setEditing(null)}>Cancel</Button>
                </div>
              </Stack>
            </Card>
          ) : null}
          {league.can_manage ? (
            <Card title="Add shooter">
              <Stack>
                <Field label="First name" htmlFor="new-given">
                  <Input
                    id="new-given"
                    value={newGiven}
                    onChange={(event) => setNewGiven(event.target.value)}
                  />
                </Field>
                <Field label="Last name" htmlFor="new-family">
                  <Input
                    id="new-family"
                    value={newFamily}
                    onChange={(event) => setNewFamily(event.target.value)}
                  />
                </Field>
                <Field label="Division" htmlFor="new-division">
                  <Select
                    id="new-division"
                    value={newDivision}
                    onChange={(event) => setNewDivision(event.target.value)}
                  >
                    <option value="">No division</option>
                    {divisions.map((division) => (
                      <option key={division.id} value={division.id}>
                        {division.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <div>
                  <Button variant="primary" busy={busyId === "new"} onClick={addShooter}>
                    Add shooter
                  </Button>
                </div>
              </Stack>
            </Card>
          ) : (
            <Notice>
              Roster edits belong to a league admin. You can approve and decline pending
              sign-ups above; everything here is read-only for a scorer.
            </Notice>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}

// --- members ------------------------------------------------------------------------------------

function MembersTab({ league }: { league: League }) {
  if (!league.can_manage) {
    return (
      <Notice>
        Only a league admin can see who has access to this league. Ask an admin for the change
        you need.
      </Notice>
    );
  }
  return <MembersInner league={league} />;
}

function MembersInner({ league }: { league: League }) {
  const members = useAsync(() => api.members(league.id), [league.id]);
  const [username, setUsername] = useState("");
  const [role, setRole] = useState<MemberRole>("scorer");
  const [confirmUserId, setConfirmUserId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  if (members.loading) return <Spinner label="Loading members" />;

  const list = members.data ?? [];
  // Orphaned: nobody holding the admin role here can sign in. The Members tab is where
  // a site admin lands to fix that, so the fix gets spelled out here.
  const orphaned = !list.some(
    (member) => member.role === "league_admin" && member.is_active,
  );

  async function add(): Promise<void> {
    if (username.trim() === "") {
      setError(new Error("Enter an account name."));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.addMember(league.id, { username: username.trim(), role });
      setUsername("");
      members.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function remove(user: { user_id: string; username: string; role: MemberRole }): Promise<void> {
    if (confirmUserId !== user.user_id) {
      setConfirmUserId(user.user_id);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.removeMember(league.id, user.user_id, user.role);
      setConfirmUserId(null);
      members.reload();
    } catch (cause) {
      setError(cause);
      setConfirmUserId(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack>
      <Card title="Members">
        <Stack>
          {members.error !== null ? <ErrorNote error={members.error} /> : null}
          {error !== null ? <ErrorNote error={error} /> : null}
          {orphaned ? (
            <Warning>
              This league has no active admin — the accounts holding that role cannot sign
              in. Re-enable one from the accounts screen, or grant the league admin role
              below to appoint a replacement. Until one exists, nothing here can be managed.
            </Warning>
          ) : null}
          {list.length === 0 ? (
            <Empty title="No members yet" note="Add an account below to give it access." />
          ) : (
            <Table
              head={["Account", "Role", "Since", null]}
              rows={list.map((member) => [
                member.username,
                <Row wrap>
                  <Badge tone={member.role === "league_admin" ? "info" : "neutral"}>
                    {member.role === "league_admin" ? "admin" : "scorer"}
                  </Badge>
                  {!member.is_active ? <Badge tone="bad">disabled</Badge> : null}
                </Row>,
                formatDay(member.created_at.slice(0, 10)),
                <Button
                  variant="danger"
                  busy={busy}
                  onClick={() => remove(member)}
                >
                  {confirmUserId === member.user_id ? "Confirm delete" : "Remove"}
                </Button>,
              ])}
            />
          )}
          <Notice>
            A scorer may enter scores and approve pending sign-ups, and nothing else on the
            roster. Everything else belongs to a league admin.
          </Notice>
        </Stack>
      </Card>
      <Card title="Add member">
        <Stack>
          <Field
            label="Account"
            htmlFor="member-username"
            hint="An existing account name; accounts are created by a site admin."
          >
            <Input
              id="member-username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          </Field>
          <Field label="Role" htmlFor="member-role">
            <Select
              id="member-role"
              value={role}
              onChange={(event) => setRole(event.target.value as MemberRole)}
            >
              <option value="league_admin">league admin</option>
              <option value="scorer">scorer</option>
            </Select>
          </Field>
          <div>
            <Button variant="primary" busy={busy} onClick={add}>
              Add member
            </Button>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}

// --- history ------------------------------------------------------------------------------------

function HistoryTab({ league }: { league: League }) {
  if (!league.can_manage) {
    return <Notice>The league log is visible to a league admin.</Notice>;
  }
  return <HistoryInner league={league} />;
}

function HistoryInner({ league }: { league: League }) {
  const feed = useAsync(() => api.audit(league.id), [league.id]);

  if (feed.loading) return <Spinner label="Loading history" />;
  if (feed.error !== null) return <ErrorNote error={feed.error} />;

  const entries = feed.data ?? [];

  return (
    <Card title="History">
      <Stack>
        <Notice>
          Clearing a score is recorded here with its previous value.
        </Notice>
        {entries.length === 0 ? (
          <Empty
            title="Nothing recorded yet"
            note="Score writes, roster decisions and settings changes appear here."
          />
        ) : (
          <List
            className="list-tight"
            items={entries.map((entry: AuditEntry) => (
              <div className="stack stack-tight">
                <Row>
                  <span className="small">{formatStamp(entry.at)}</span>
                  <span className="small">{entry.actor_username ?? "anonymous"}</span>
                  <Badge tone="neutral">{entry.action}</Badge>
                  <span className="small muted">{entry.entity}</span>
                </Row>
                <p className="small muted mono">
                  {`before ${clipJson(entry.before)} — after ${clipJson(entry.after)}`}
                </p>
              </div>
            ))}
          />
        )}
      </Stack>
    </Card>
  );
}

// --- site admin: accounts ------------------------------------------------------------------

export function AccountsPage() {
  const auth = useAuth();

  if (auth.loading) return <Spinner label="Checking session" />;

  if (auth.user === null) {
    return (
      <Card title="Accounts">
        <Notice>
          Accounts are created and managed by a site admin. <Link to="/login">Log in</Link> with
          one.
        </Notice>
      </Card>
    );
  }
  if (!auth.user.is_site_admin) {
    return (
      <Card title="Accounts">
        <Notice>
          Only a site admin can see or manage accounts. For a password reset or a new login, ask
          one — that is what they are for.
        </Notice>
      </Card>
    );
  }
  return <AccountsInner me={auth.user.id} onSelfReset={auth.refresh} />;
}

function AccountsInner({ me, onSelfReset }: { me: string; onSelfReset: () => Promise<void> }) {
  const accounts = useAsync(() => api.accounts(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);

  const [resetId, setResetId] = useState<string | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetForce, setResetForce] = useState(true);

  const [newUsername, setNewUsername] = useState("");
  const [newDisplay, setNewDisplay] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newAdmin, setNewAdmin] = useState(false);

  if (accounts.loading) return <Spinner label="Loading accounts" />;
  const list = accounts.data ?? [];
  const resetting = list.find((account) => account.id === resetId);

  async function run(id: string, work: () => Promise<{ warning?: string } | void>): Promise<void> {
    setBusyId(id);
    setError(null);
    setNote(null);
    try {
      // A returned `warning` is the server's short-password note; work that has its own
      // sentence for the card sets it inside the closure.
      const result = await work();
      if (result && result.warning) setNote(result.warning);
      accounts.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusyId(null);
    }
  }

  function toggleActive(account: Account): void {
    if (account.is_active && confirmDisableId !== account.id) {
      setConfirmDisableId(account.id);
      return;
    }
    void run(account.id, () =>
      api.patchAccount(account.id, { is_active: !account.is_active }).then(() => undefined),
    ).then(() => setConfirmDisableId(null));
  }

  function toggleAdmin(account: Account): void {
    void run(account.id, () =>
      api.patchAccount(account.id, { is_site_admin: !account.is_site_admin }).then(() => undefined),
    );
  }

  function reset(): void {
    if (resetId === null) return;
    if (resetPassword === "") {
      setError(new Error("Type the new password — clearing is not what this does."));
      return;
    }
    const target = resetId;
    void run(target, async () => {
      const result = await api.resetAccountPassword(target, {
        password: resetPassword,
        must_change_password: resetForce,
      });
      setResetId(null);
      setResetPassword("");
      if (target === me) {
        // The reset closed this very session; say so and let the shell see it.
        setNote("Your password is set, and this session is closed with the others. Sign in again.");
        await onSelfReset();
      } else if (!result.warning) {
        setNote("Password set. Every session they held is signed out.");
      }
      return result;
    });
  }

  function create(): void {
    if (newUsername.trim() === "") {
      setError(new Error("Pick a username — it is how they sign in."));
      return;
    }
    if (newPassword === "") {
      setError(new Error("A new account needs a starting password to hand over."));
      return;
    }
    void run("new", async () => {
      const created = await api.createAccount({
        username: newUsername.trim(),
        display_name: newDisplay.trim() === "" ? undefined : newDisplay.trim(),
        password: newPassword,
        is_site_admin: newAdmin,
        must_change_password: true,
      });
      setNewUsername("");
      setNewDisplay("");
      setNewPassword("");
      setNewAdmin(false);
      return created;
    });
  }

  return (
    <Stack>
      <Card title="Accounts">
        <Stack>
          {accounts.error !== null ? <ErrorNote error={accounts.error} /> : null}
          {error !== null ? <ErrorNote error={error} onDismiss={() => setError(null)} /> : null}
          {note !== null ? <Warning>{note}</Warning> : null}
          <Table
            head={["Account", null, "Reach", null]}
            rows={list.map((account) => [
              <Stack tight>
                <span>{account.username}</span>
                <span className="small muted">{account.display_name}</span>
              </Stack>,
              <Row wrap>
                {account.id === me ? <Badge tone="neutral">you</Badge> : null}
                {account.is_site_admin ? <Badge tone="info">site admin</Badge> : null}
                {account.must_change_password ? <Badge tone="warn">must change</Badge> : null}
                {!account.is_active ? <Badge tone="bad">disabled</Badge> : null}
              </Row>,
              <span className="small muted num">
                {account.league_memberships === 1 ? "1 league" : `${account.league_memberships} leagues`}
                {" · "}
                {account.live_sessions === 1 ? "1 session" : `${account.live_sessions} sessions`}
              </span>,
              <Row wrap>
                <Button
                  onClick={() => {
                    setResetId(account.id);
                    setResetPassword("");
                    setResetForce(true);
                    setError(null);
                  }}
                >
                  Password
                </Button>
                {account.id !== me ? (
                  <Button
                    variant={account.is_active ? "danger" : "ghost"}
                    busy={busyId === account.id}
                    onClick={() => toggleActive(account)}
                  >
                    {account.is_active
                      ? confirmDisableId === account.id
                        ? "Confirm disable"
                        : "Disable"
                      : "Enable"}
                  </Button>
                ) : null}
                {account.id !== me ? (
                  <Button
                    busy={busyId === account.id}
                    onClick={() => toggleAdmin(account)}
                  >
                    {account.is_site_admin ? "Strip admin" : "Make admin"}
                  </Button>
                ) : null}
              </Row>,
            ])}
          />
          <Notice>
            Disabling closes live sessions at once and refuses the sign-in door; the account
            keeps its memberships and its audit history, so nothing that happened is un-sent.
            League roles live on each league's Members tab.
          </Notice>
        </Stack>
      </Card>
      {resetting !== undefined ? (
        <Card title={`Set password — ${resetting.username}`}>
          <Stack>
            <Field
              label="New password"
              htmlFor="account-reset-password"
              hint="Hand it over and let them change it. Below the length the app enforces, it warns and lets the forced change fix it."
            >
              <Input
                id="account-reset-password"
                type="text"
                autoComplete="new-password"
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
              />
            </Field>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={resetForce}
                onChange={(event) => setResetForce(event.target.checked)}
              />
              Force a change at their next sign-in
            </label>
            <Row>
              <Button variant="primary" busy={busyId === resetId} onClick={reset}>
                Set password
              </Button>
              <Button onClick={() => setResetId(null)}>Cancel</Button>
            </Row>
            <Notice>
              This signs every holder of this account out, you included if it is your own.
            </Notice>
          </Stack>
        </Card>
      ) : null}
      <Card title="New account">
        <Stack>
          <Field
            label="Username"
            htmlFor="new-username"
            hint="Lowercase, how they will type it at the sign-in screen."
          >
            <Input
              id="new-username"
              value={newUsername}
              onChange={(event) => setNewUsername(event.target.value)}
            />
          </Field>
          <Field label="Display name" htmlFor="new-display" hint="Optional; the username is the fallback.">
            <Input
              id="new-display"
              value={newDisplay}
              onChange={(event) => setNewDisplay(event.target.value)}
            />
          </Field>
          <Field
            label="Starting password"
            htmlFor="new-password"
            hint="Always with a forced change: what you type here is a handover secret, not their password."
          >
            <Input
              id="new-password"
              type="text"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={newAdmin}
              onChange={(event) => setNewAdmin(event.target.checked)}
            />
            Site admin — may manage accounts and every league
          </label>
          <div>
            <Button variant="primary" busy={busyId === "new"} onClick={create}>
              Create account
            </Button>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}
